import {
  ScheduledQueryDay,
  ScheduledQueryFrequency,
  ScheduledQueryOccurrence,
  ScheduledQueryWindow,
} from '@api/api/scheduled-queries';
import { useTranslation } from 'react-i18next';

import styles from './ScheduleOptionsRow.module.scss';
import { ScheduleSelectField } from './ScheduleSelectField';

const FREQUENCY_OPTIONS: ScheduledQueryFrequency[] = [
  'daily',
  'weekly',
  'monthly',
];
const DAY_OPTIONS: ScheduledQueryDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const OCCURRENCE_OPTIONS: ScheduledQueryOccurrence[] = [
  'first',
  'second',
  'third',
  'fourth',
  'last',
];
const DELIVERY_TIME_OPTIONS: ScheduledQueryWindow[] = [
  'morning',
  'afternoon',
  'evening',
];

export interface ScheduleOptionsValue {
  frequency: ScheduledQueryFrequency;
  day: ScheduledQueryDay;
  occurrence: ScheduledQueryOccurrence;
  deliveryTime: ScheduledQueryWindow;
}

type ScheduleOptionsChangeHandler = {
  (field: 'frequency', value: ScheduledQueryFrequency): void;
  (field: 'day', value: ScheduledQueryDay): void;
  (field: 'occurrence', value: ScheduledQueryOccurrence): void;
  (field: 'deliveryTime', value: ScheduledQueryWindow): void;
};

export interface ScheduleOptionsRowProps {
  value: ScheduleOptionsValue;
  onChange: ScheduleOptionsChangeHandler;
}

function resolveOption<T extends string>(
  options: T[],
  raw: string
): T | undefined {
  return options.find(o => o === raw);
}

export const ScheduleOptionsRow = ({
  value,
  onChange,
}: ScheduleOptionsRowProps) => {
  const { t } = useTranslation(['chat']);

  const handleFrequencyChange = (raw: string) => {
    const freq = resolveOption(FREQUENCY_OPTIONS, raw);
    if (!freq) {
      return;
    }
    onChange('frequency', freq);
    if (freq === 'monthly') {
      onChange('occurrence', 'first');
    }
  };

  return (
    <div className={styles.scheduleRow}>
      <ScheduleSelectField
        label={t('chat:scheduledQuerySettings.frequency')}
        options={FREQUENCY_OPTIONS}
        value={value.frequency}
        translationKeyPrefix="chat:scheduledQuerySettings.frequencyOptions"
        testId="scheduled-query-frequency"
        onChange={handleFrequencyChange}
      />

      {value.frequency === 'weekly' && (
        <ScheduleSelectField
          label={t('chat:scheduledQuerySettings.day')}
          options={DAY_OPTIONS}
          value={value.day}
          translationKeyPrefix="chat:scheduledQuerySettings.dayOptions"
          testId="scheduled-query-day"
          onChange={raw => {
            const day = resolveOption(DAY_OPTIONS, raw);
            if (day) {
              onChange('day', day);
            }
          }}
        />
      )}

      {value.frequency === 'monthly' && (
        <>
          <ScheduleSelectField
            label={t('chat:scheduledQuerySettings.occurrence')}
            options={OCCURRENCE_OPTIONS}
            value={value.occurrence}
            translationKeyPrefix="chat:scheduledQuerySettings.occurrenceOptions"
            testId="scheduled-query-occurrence"
            onChange={raw => {
              const occ = resolveOption(OCCURRENCE_OPTIONS, raw);
              if (occ) {
                onChange('occurrence', occ);
              }
            }}
          />
          <ScheduleSelectField
            label={t('chat:scheduledQuerySettings.day')}
            options={DAY_OPTIONS}
            value={value.day}
            translationKeyPrefix="chat:scheduledQuerySettings.dayOptions"
            testId="scheduled-query-day-monthly"
            onChange={raw => {
              const day = resolveOption(DAY_OPTIONS, raw);
              if (day) {
                onChange('day', day);
              }
            }}
          />
        </>
      )}

      <ScheduleSelectField
        label={t('chat:scheduledQuerySettings.deliveryTime')}
        options={DELIVERY_TIME_OPTIONS}
        value={value.deliveryTime}
        translationKeyPrefix="chat:scheduledQuerySettings.deliveryTimeOptions"
        testId="scheduled-query-delivery-time"
        onChange={raw => {
          const time = resolveOption(DELIVERY_TIME_OPTIONS, raw);
          if (time) {
            onChange('deliveryTime', time);
          }
        }}
      />
    </div>
  );
};
